/**
 * Einklappzustand der Seitenleiste - `localStorage`, ueberlebt einen Neubau
 * der Seite (Paket U3, Auftragszeile "Zustand in localStorage").
 *
 * Eigene, kleine Datei statt Zustand direkt in `Sidebar.tsx`: So laesst sich
 * das Ueberleben pruefen, ohne die Komponente zu rendern (kein
 * `@testing-library/react` noetig, `jsdom` allein genuegt fuer
 * `localStorage`).
 */

const SCHLUESSEL = 'tuv-seitenleiste-eingeklappt';

/** Liest den gespeicherten Zustand. Fehlt er oder ist die Umgebung ohne
 *  `localStorage` (z. B. sehr alte Server-Rendering-Faelle), gilt: ausgeklappt. */
export function ladeEingeklappt(): boolean {
  try {
    return globalThis.localStorage?.getItem(SCHLUESSEL) === '1';
  } catch {
    return false;
  }
}

/** Speichert den Zustand fuer den naechsten Aufbau. */
export function speichereEingeklappt(eingeklappt: boolean): void {
  try {
    globalThis.localStorage?.setItem(SCHLUESSEL, eingeklappt ? '1' : '0');
  } catch {
    // Kein Speicher verfuegbar (privater Modus, Speicher voll) - der
    // Zustand bleibt dann eben nur fuer die laufende Sitzung erhalten.
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
   ZUGEKLAPPTE GRUPPEN
   ═══════════════════════════════════════════════════════════════════════════

   Abschnitt 5 des Auftrags verlangt aufklappbare Untergruppen. Gespeichert
   wird, welche Gruppen ZUGEKLAPPT sind - nicht, welche offen sind. Der
   Unterschied zählt: Kommt später eine neue Gruppe dazu, ist sie damit
   offen statt unsichtbar. Beim umgekehrten Speicherformat wäre jede neue
   Gruppe für alle bestehenden Benutzer zunächst versteckt - genau der
   stille Funktionsverlust, den Abschnitt 43 verbietet.
*/

const GRUPPEN_SCHLUESSEL = 'tuv-seitenleiste-zu-gruppen';

export function ladeZugeklappteGruppen(): string[] {
  try {
    const roh = globalThis.localStorage?.getItem(GRUPPEN_SCHLUESSEL);
    if (!roh) return [];
    const gelesen: unknown = JSON.parse(roh);
    return Array.isArray(gelesen) ? gelesen.filter((e): e is string => typeof e === 'string') : [];
  } catch {
    // Unlesbarer Eintrag (Format aus einer früheren Fassung, abgeschnittener
    // Speicher): lieber alles offen als eine leere Seitenleiste.
    return [];
  }
}

export function speichereZugeklappteGruppen(gruppen: readonly string[]): void {
  try {
    globalThis.localStorage?.setItem(GRUPPEN_SCHLUESSEL, JSON.stringify(gruppen));
  } catch {
    // Kein Speicher verfügbar - der Zustand gilt dann nur für diese Sitzung.
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
   OFFENE GEWERKE
   ═══════════════════════════════════════════════════════════════════════════

   Die Gruppe GEWERKE ist zweistufig (SL2). Gespeichert wird, welche Gewerke
   von Hand GEOEFFNET wurden - hier andersherum als bei den Gruppen, und aus
   demselben Grund: Der Ruhezustand eines Gewerks ist ZU. Ein neu
   hinzukommendes Gewerk waere beim umgekehrten Format fuer alle bestehenden
   Benutzer sofort aufgeklappt, und die Leiste waere wieder so lang wie die
   flache Fassung, die SL1/SL2 gerade abgeloest haben.

   Das Gewerk, in dem man gerade steht, steht hier NICHT drin - es ist
   ohnehin immer offen (siehe `Sidebar.tsx`). Sonst bliebe jedes einmal
   besuchte Gewerk fuer immer aufgeklappt.
*/

const GEWERKE_SCHLUESSEL = 'tuv-seitenleiste-offene-gewerke';

export function ladeOffeneGewerke(): string[] {
  try {
    const roh = globalThis.localStorage?.getItem(GEWERKE_SCHLUESSEL);
    if (!roh) return [];
    const gelesen: unknown = JSON.parse(roh);
    return Array.isArray(gelesen) ? gelesen.filter((e): e is string => typeof e === 'string') : [];
  } catch {
    // Unlesbarer Eintrag: lieber alles zu als eine kaputte Leiste.
    return [];
  }
}

export function speichereOffeneGewerke(gewerke: readonly string[]): void {
  try {
    globalThis.localStorage?.setItem(GEWERKE_SCHLUESSEL, JSON.stringify(gewerke));
  } catch {
    // Kein Speicher verfuegbar - der Zustand gilt dann nur fuer diese Sitzung.
  }
}
